import { AutoProduct } from 'src/enums/AutoProduct'

const ILLOGICAL_FLOW_ERROR_CODES = [
  'ILLOGICAL_FLOW_POLICY_EXISTS',
  'ILLOGICAL_FLOW_PAYMENT_DONE',
  'ILLOGICAL_FLOW_POLICY_EXPIRED_RENEWAL_DUE',
  'ILLOGICAL_FLOW_UNACCEPTABLE_ASSET',
]

const DEFAULT_API_ERROR_MESSAGE = 'An error occurred. Please try again.'
const INVALID_REGISTRATION_ERROR_MESSAGE = 'Invalid registration number. Please try again.'
const NOT_AVAILABLE_URL = '/not-available'

type ProposalApiData = Record<string, any>

export type BikeProposalResolution =
  | {
      type: 'redirect'
      redirectUrl: string
      proposalData: ProposalApiData
    }
  | {
      type: 'illogical_flow'
      errorCode: string
      illogicalFlowData: ProposalApiData
      proposalData: ProposalApiData
    }
  | {
      type: 'error'
      message: string
      data?: ProposalApiData
    }
  | {
      type: 'not_available'
      redirectUrl: string
      data?: ProposalApiData
    }
  | {
      type: 'noop'
      proposalData: ProposalApiData
    }

const isObject = (value: unknown): value is ProposalApiData =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const parseJsonObject = (value: unknown): ProposalApiData | null => {
  if (isObject(value)) return value
  if (typeof value !== 'string') return null

  try {
    const parsedValue = JSON.parse(value)
    return isObject(parsedValue) ? parsedValue : null
  } catch {
    return null
  }
}

const getErrorData = (error: any): ProposalApiData => {
  const responseData = parseJsonObject(error?.response?.data)
  if (responseData) return responseData

  const requestData = parseJsonObject(error?.request?.response)
  if (requestData) return requestData

  return {}
}

const getErrorString = (error: any, errorData: ProposalApiData) => {
  if (Object.keys(errorData).length) return JSON.stringify(errorData).toUpperCase()
  return JSON.stringify(error?.request?.response || error?.response?.data || {}).toUpperCase()
}

const getBikeJourneyFlow = (data?: ProposalApiData): string => {
  const variantId = data?.variantId ?? data?.variant_id
  if (variantId !== undefined && variantId !== null && variantId !== '') {
    return 'found'
  }
  return ''
}

const getBikeJourneyUrl = (proposalId: string, data?: ProposalApiData) => {
  const params = new URLSearchParams({
    proposal_id: proposalId,
    src: 'lp',
    flow: getBikeJourneyFlow(data),
  })
  return `/bike-journey/nsr?${params.toString()}`
}

const getIllogicalFlowData = (data: ProposalApiData, registrationNumber: string, fallbackErrorCode?: string) => {
  const rawErrorContext = data?.error_context
  const errorContext = isObject(rawErrorContext) ? rawErrorContext : {}
  const detectedVehicleType =
    typeof rawErrorContext === 'string' ? rawErrorContext : errorContext.vehicle_type || data?.vehicle_type
  const errorCode = data?.error_code || fallbackErrorCode || ''

  return {
    ...data,
    ...errorContext,
    error_code: errorCode,
    message: data?.error_message || data?.message || (errorCode ? `Error: ${errorCode}` : DEFAULT_API_ERROR_MESSAGE),
    registration_number: errorContext.registration_number || data?.registration_number || registrationNumber,
    user_logged_in: errorContext.user_logged_in ?? data?.user_logged_in ?? false,
    last_digits: errorContext.last_digits || data?.last_digits || '',
    redirect_url: errorContext.redirect_url || data?.redirect_url || null,
    vehicle_type: detectedVehicleType || AutoProduct.Bike,
  }
}

const getIllogicalFlowResolution = (
  data: ProposalApiData,
  registrationNumber: string,
  fallbackErrorCode?: string
): BikeProposalResolution => {
  const illogicalFlowData = getIllogicalFlowData(data, registrationNumber, fallbackErrorCode)

  return {
    type: 'illogical_flow',
    errorCode: illogicalFlowData.error_code,
    illogicalFlowData,
    proposalData: data,
  }
}

export const resolveBikeProposalResponse = (
  registrationNumber: string,
  data: ProposalApiData = {}
): BikeProposalResolution => {
  if (data?.error_code === 'MODEL_UNSERVICEABLE') {
    return {
      type: 'not_available',
      redirectUrl: NOT_AVAILABLE_URL,
      data,
    }
  }

  if (data?.error_code) {
    if (ILLOGICAL_FLOW_ERROR_CODES.includes(data.error_code)) {
      return getIllogicalFlowResolution(data, registrationNumber)
    }

    return {
      type: 'error',
      message: data.error_message || data.message || DEFAULT_API_ERROR_MESSAGE,
      data,
    }
  }

  if (data?.ekey) {
    return {
      type: 'redirect',
      redirectUrl: getBikeJourneyUrl(data.ekey, data),
      proposalData: data,
    }
  }

  return {
    type: 'noop',
    proposalData: data,
  }
}

export const resolveBikeProposalError = (error: any, _registrationNumber: string): BikeProposalResolution => {
  const errorData = getErrorData(error)
  const errorString = getErrorString(error, errorData)

  if (errorString.includes('MODEL_UNSERVICEABLE')) {
    return {
      type: 'not_available',
      redirectUrl: NOT_AVAILABLE_URL,
      data: errorData,
    }
  }

  if (error?.response?.status === 400 || error?.request?.status === 400) {
    return {
      type: 'error',
      message: errorData?.error_message || INVALID_REGISTRATION_ERROR_MESSAGE,
      data: errorData,
    }
  }

  return {
    type: 'error',
    message: error?.message || DEFAULT_API_ERROR_MESSAGE,
    data: errorData,
  }
}
